import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Chip,
  NakedButton,
  PopoverMenu,
  PopoverMenuItem,
  SpinningLoader,
  usePopoverMenu,
  useTooltip,
} from '@frontend/design-system';
import { AppointmentStatusType, AppointmentStatusV3Enum } from './types';

type AppointmentStatusChipProps = {
  isLoadingStatus?: boolean;
  isLoadingList?: boolean;
  isEditable?: boolean;
  isIntegratedOffice?: boolean;
  selectedStatus: AppointmentStatusType;
  statusList: AppointmentStatusType[];
  onChange: (status: AppointmentStatusType) => void;
};

// TODO: Add Integrated Location support for AppointmentStatusComponents.EditableStatusChip
// and Replace this AppointmentStatusChip with AppointmentStatusComponents.EditableStatusChip
export const AppointmentStatusChip = ({
  isLoadingStatus,
  isLoadingList,
  isEditable,
  isIntegratedOffice,
  selectedStatus,
  statusList,
  onChange,
}: AppointmentStatusChipProps) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();

  const popoverMenu = usePopoverMenu({ placement: 'bottom-start' });

  if (isLoadingStatus) {
    return <SpinningLoader size='xs' />;
  }

  if (!isEditable) {
    return (
      <>
        <Chip
          {...triggerProps}
          variant={selectedStatus.variant}
          css={{ minWidth: 'fit-content' }}
          leftElement={
            selectedStatus.iconName && <Icon name={selectedStatus.iconName} css={{ color: 'currentcolor' }} />
          }
        >
          {selectedStatus.text}
        </Chip>
        <Tooltip {...tooltipProps}>{t('Appointment status writeback is not supported')}</Tooltip>
      </>
    );
  }

  return (
    <>
      <NakedButton css={{ maxWidth: 'max-content' }} {...popoverMenu.getTriggerProps()}>
        <Chip
          variant={selectedStatus.variant}
          css={{ width: 'auto', maxWidth: '200px', textTransform: 'capitalize' }}
          leftElement={
            selectedStatus.iconName && <Icon name={selectedStatus.iconName} css={{ color: 'currentcolor' }} />
          }
        >
          {selectedStatus.text ? selectedStatus.text : selectedStatus.key?.toLowerCase().replaceAll('_', ' ')}
        </Chip>
      </NakedButton>
      <PopoverMenu {...popoverMenu.getMenuProps()}>
        {isLoadingList ? (
          <SpinningLoader size='xs' />
        ) : (
          statusList?.map((status, index) => {
            if (status.key === AppointmentStatusV3Enum.UNKNOWN_EVENT_STATUS) {
              // Skip the UNKNOWN_EVENT_STATUS from the list
              return null;
            }
            const { key, text, value, iconName } = status;
            const label = isIntegratedOffice ? `${key} / ${text}` : text ?? '';
            const isSelected = selectedStatus.key === key;

            return (
              <PopoverMenuItem
                key={`${key}-${value}-${index}`}
                value={value}
                css={[{ textTransform: 'capitalize' }, isSelected && { backgroundColor: theme.colors.neutral10 }]}
                {...popoverMenu.getItemProps({
                  index,
                  onClick: () => onChange(status),
                })}
              >
                {iconName && <Icon name={iconName} />}
                {label.toLowerCase().replaceAll('_', ' ')}
              </PopoverMenuItem>
            );
          })
        )}
      </PopoverMenu>
    </>
  );
};
